import React, { useState } from 'react';
import { Phone, Mail, User, Award, Users, ChevronRight, Instagram, MessageSquare } from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';
import { coachesByAgeGroup } from '../mock';

// TikTok Icon Component
const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Interactive Flip Card Component
const FlipCard = ({ coach }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const getAvatarColor = (ageGroup) => {
    const colors = {
      '6u': '#FF6B6B',
      '7u': '#4ECDC4',
      '8u': '#45B7D1',
      '10u': '#96CEB4',
      '11u': '#FFEAA7',
      '12u': '#DDA0DD',
      '13u': '#98D8C8',
      'cheer': '#FFB6C1',
      'flag': '#87CEEB'
    };
    const lower = ageGroup.toLowerCase();
    let key;
    if (lower.includes('flag')) {
      key = 'flag';
    } else if (lower.includes('cheer')) {
      key = 'cheer';
    } else {
      key = lower;
    }
    return colors[key] || '#FFD700';
  };

  const getInitials = (name) => {
    if (!name || name === 'TBA') return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <div 
      className={`flip-card ${isFlipped ? 'flipped' : ''}`}
      onClick={() => setIsFlipped(!isFlipped)}
      data-testid={`coach-card-${coach.id}`}
    >
      <div className="flip-card-inner">
        {/* Front of card */}
        <div className="flip-card-front">
          {coach.image ? (
            <div className="coach-avatar-photo">
              <img src={coach.image} alt={coach.headCoach} />
            </div>
          ) : (
            <div 
              className="coach-avatar-circle"
              style={{ backgroundColor: getAvatarColor(coach.ageGroup) }}
            >
              <span className="avatar-initials">{getInitials(coach.headCoach)}</span>
            </div>
          )}
          <h3 className="coach-name">{coach.headCoach}</h3>
          <div className="coach-team-badge">{coach.ageGroup}</div>
          <div className="flip-hint">
            <span>Tap for details</span>
            <ChevronRight size={16} />
          </div>
        </div>

        {/* Back of card */}
        <div className="flip-card-back">
          <div className="back-header">
            {coach.image ? (
              <div className="back-avatar-small">
                <img src={coach.image} alt={coach.headCoach} />
              </div>
            ) : (
              <User size={24} />
            )}
            <h4>{coach.headCoach}</h4>
          </div>
          <div className="back-role">
            <Award size={16} />
            <span>{coach.ageGroup} Head Coach</span>
          </div>
          
          {/* Coach Bio Section */}
          <div className="coach-bio">
            <p>{coach.bio || "Bio coming soon..."}</p>
          </div>

          {coach.assistantCoaches && coach.assistantCoaches.length > 0 && (
            <div className="back-assistants">
              <Users size={16} />
              <span>Assistants: {coach.assistantCoaches.join(', ')}</span>
            </div>
          )}

          <div className="back-contact">
            {coach.phone && coach.phone !== "(XXX) XXX-XXXX" && coach.phone !== "" && (
              <a 
                href={`tel:${coach.phone}`} 
                className="contact-link"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone size={16} />
                <span>{coach.phone}</span>
              </a>
            )}
            {coach.smsRecruit && (
              <a
                href={`sms:${coach.smsRecruit.phone}?&body=${encodeURIComponent(coach.smsRecruit.prefilledMessage)}`}
                className="contact-link"
                onClick={(e) => e.stopPropagation()}
                data-testid={`coach-sms-${coach.id}`}
              >
                <MessageSquare size={16} />
                <span>Text Coach Directly</span>
              </a>
            )}
            {coach.email && (
              <a 
                href={`mailto:${coach.email}`} 
                className="contact-link"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail size={16} />
                <span>Send Email</span>
              </a>
            )}
          </div>

          {/* Social Media Links */}
          {coach.social && (
            <div className="back-social">
              {coach.social.instagram && (
                <a 
                  href={coach.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link-labeled instagram"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Instagram size={20} />
                  <span>Instagram</span>
                </a>
              )}
              {coach.social.tiktok && (
                <a 
                  href={coach.social.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link-labeled tiktok"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TikTokIcon size={20} />
                  <span>TikTok</span>
                </a>
              )}
            </div>
          )}

          <div className="flip-hint back-hint">
            <span>Tap to flip back</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CoachesPage = () => {
  // Group coaches by type
  const footballCoaches = coachesByAgeGroup.filter(c => 
    !c.ageGroup.toLowerCase().includes('cheer') && 
    !c.ageGroup.toLowerCase().includes('flag')
  );
  const girlsFlagCoaches = coachesByAgeGroup.filter(c =>
    c.ageGroup.toLowerCase().includes('girls flag')
  );
  const flagCoaches = coachesByAgeGroup.filter(c => 
    c.ageGroup.toLowerCase().includes('flag') &&
    !c.ageGroup.toLowerCase().includes('girls flag')
  );
  const cheerCoaches = coachesByAgeGroup.filter(c => 
    c.ageGroup.toLowerCase().includes('cheer')
  );

  return (
    <div className="page-container">
      <Navigation />
      
      <main className="main-content">
        <section className="page-hero">
          <div className="container">
            <h1 className="page-title">Our Coaches</h1>
            <p className="page-subtitle">Meet the dedicated leaders behind our teams</p>
          </div>
        </section>

        {/* Football Coaches */}
        <section className="coaches-section" data-testid="football-coaches-section">
          <div className="container">
            <h2 className="section-title">Tackle Football Coaches</h2>
            <p className="section-subtitle">Leading our football teams to victory</p>
            <div className="coaches-flip-grid">
              {footballCoaches.map((coach) => (
                <FlipCard key={coach.id} coach={coach} />
              ))}
            </div>
          </div>
        </section>

        {/* Flag Football Coaches */}
        {flagCoaches.length > 0 && (
          <section className="coaches-section flag-section" data-testid="flag-coaches-section">
            <div className="container">
              <h2 className="section-title">Flag Football Coaches</h2>
              <p className="section-subtitle">NFL FLAG program leadership</p>
              <div className="coaches-flip-grid">
                {flagCoaches.map((coach) => (
                  <FlipCard key={coach.id} coach={coach} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Girls Flag Football Coaches — dedicated section for our newest program */}
        {girlsFlagCoaches.length > 0 && (
          <section
            className="coaches-section flag-section"
            data-testid="girls-flag-coaches-section"
            style={{
              background:
                'radial-gradient(circle at 20% 10%, rgba(255, 215, 0, 0.14) 0%, transparent 45%), ' +
                'linear-gradient(155deg, #050505 0%, #150a10 55%, #050505 100%)',
            }}
          >
            <div className="container">
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <span style={{
                  display: 'inline-block',
                  background: 'linear-gradient(90deg, #FFD700 0%, #FFC500 100%)',
                  color: '#000',
                  padding: '0.3rem 0.85rem',
                  borderRadius: '999px',
                  fontSize: '0.7rem',
                  fontWeight: 900,
                  letterSpacing: '2.5px',
                  marginBottom: '0.85rem',
                  boxShadow: '0 6px 18px rgba(255, 215, 0, 0.4)',
                }}>
                  ✨ NEWEST PROGRAM
                </span>
                <h2 className="section-title" style={{ color: '#FFD700' }}>Girls Flag Football Coaches</h2>
                <p className="section-subtitle">Ages 12-14 · Tap the card to text or call directly</p>
              </div>
              <div className="coaches-flip-grid">
                {girlsFlagCoaches.map((coach) => (
                  <FlipCard key={coach.id} coach={coach} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Cheer Coaches */}
        {cheerCoaches.length > 0 && (
          <section className="coaches-section cheer-section" data-testid="cheer-coaches-section">
            <div className="container">
              <h2 className="section-title">Cheer Coaches</h2>
              <p className="section-subtitle">Bringing spirit and energy to our sidelines</p>
              <div className="coaches-flip-grid">
                {cheerCoaches.map((coach) => (
                  <FlipCard key={coach.id} coach={coach} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Join Our Team CTA */}
        <section className="coaches-cta-section">
          <div className="container">
            <div className="coaches-cta">
              <h2>Interested in Coaching?</h2>
              <p>We're always looking for dedicated individuals to join our coaching staff. Make a difference in young athletes' lives!</p>
              <a href="mailto:info@randallstownpanthers.com" className="btn-primary btn-large">
                Contact Us <Mail size={18} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CoachesPage;
